import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { clientQuery, clientMutation } from "Core/data/GraphQLApi";
import { endpoints } from "Core/defaultValues";
import { ExceptionManager } from "Core/logManager"

const INIT_STATE = {
    loadingRoutes: false,
    restart: false,
    routes: { items: [], total: 0},
    loadingRoute: false,
    filters: {
      openSearch: "",
      limit: 10,
      offset: 0
    },
    erorrRoutes: null,
    searchRoutesTracker: false,
    avatarFilesDriversPresigned: [] //array of avatar files for drivers in the routes
};

const route = "settings/views/routesTracker";

export const routesTrackerSearchRoutes = createAsyncThunk('routes/routesTracker/routesTrackerSearchRoutes', async ({ openSearch, limit = 10, offset = 0, paramConditions = [] }, { rejectWithValue, dispatch }) => {
    try {

        let conditions = []

        conditions.push({
            fields: ["isTemplate"],
            terms: ["0"],
            exact: 1
        })

        if(openSearch){
            conditions.push({
                fields: ["title"],
                terms: [openSearch]
            })
        }

        if(paramConditions.length > 0){
            conditions = [...conditions, ...paramConditions]
        }

        const sort = {
            field: "title",
            order: "ASC"
        };
        const response = await clientQuery(
            `
            query searchRoutes (
                $conditions: [RouteSearchInput],
                $sort: [RouteSortInput], 
                $limit: Int, 
                $offset: Int
              ) {
                Routes: searchRoutes (conditions: $conditions, sort: $sort, limit: $limit, offset: $offset) {
                  items {
                    id
                    title
                    color
                    optimized
                    avoidToll

                    isTemplate
                    templateRouteId
                    driverId
                    scheduledAt

                    distanceMeters
                    status
                    pointsCount

                    driver{
                        name
                        contactInfo
                        driverId
                        avatarFile {
                            id
                            repositoryPath
                            fileKey
                        }
                        unit {
                            label
                            id
                        }
                    }   

                    startPoint
                    endPoint
                    stopsVisitedCount
                    stopsPendingCount
                    pointsCount

                    points {
                        id
                        name
                        routeId
                        address
                        city
                        state
                        landmarkId
                        latitude
                        longitude
                        sequence
                        note
                        radius
                        status
                        typePoint
                    }

                  }
                  total
                }
              }
            `,
            {
                conditions: conditions,
                sort, 
                limit, 
                offset
            },
            endpoints.GRAPHQL_GENERAL_V2
        );
        return response?.Routes || { items: [], total: 0 }; 

    } catch (exc) {
        ExceptionManager(exc, route, 'routesTrackerSearchRoutes');
        return rejectWithValue(exc)
    }
}, {
    condition: (_, { getState }) => {
        const { routesTrackerRedux } = getState();
        return !routesTrackerRedux.loadingRoutes;
    }
});

export const routesTrackerSearchRoutesWithFilters = createAsyncThunk('routes/routesTracker/routesTrackerSearchRoutesWithFilters', async ({ conditions, limit = 10, offset = 0 }, { rejectWithValue, dispatch }) => {
    try {
        const sort = {
            field: "title",
            order: "ASC"
        };

        const response = await clientQuery(
            `
            query searchRoutes (
                $conditions: [RouteSearchInput],
                $sort: [RouteSortInput], 
                $limit: Int, 
                $offset: Int
              ) {
                Routes: searchRoutes (conditions: $conditions, sort: $sort, limit: $limit, offset: $offset) {
                  items {
                    id
                    title
                    color

                    isTemplate
                    templateRouteId
                    driverId
                    scheduledAt

                    distanceMeters
                    status
                    

                    driver{
                        name
                        avatarFile {
                            id
                            repositoryPath
                            fileKey
                        }
                        unit {
                            label
                            id
                        }
                    }

                    startPoint
                    endPoint
                    stopsVisitedCount
                    stopsPendingCount
                    pointsCount

                    points {
                        id
                        name
                        routeId
                        sequence
                        status
                        typePoint
                    }

                  }
                  total
                }
              }
            `,
            {
                conditions: conditions,
                sort, 
                limit, 
                offset
            },
            endpoints.GRAPHQL_GENERAL_V2
        );

        //send to search for driver images
        //but only if the avatarFile attribute has data
        const itemsWithAvatar = response?.Routes?.items?.filter(item => item.driver?.avatarFile?.fileKey && !item.driver?.avatarFile?.presignedUrl);
        if(itemsWithAvatar && itemsWithAvatar.length > 0){
            dispatch(getPresignedUrlsForDriverImages(itemsWithAvatar || []));
        }

        return response?.Routes || { items: [], total: 0 }; 

    } catch (exc) {
        ExceptionManager(exc, route, 'routesTrackerSearchRoutesWithFilters');
        return rejectWithValue(exc)
    }
}, {
    condition: (_, { getState }) => {
        const { routesTrackerRedux } = getState();
        return !routesTrackerRedux.loadingRoutes;
    }
});

export const getPresignedUrlsForDriverImages = createAsyncThunk(
  "layerMaps/redux/slice/getPresignedUrlsForDriverImages",
  async (fileDataArray, { rejectWithValue }) => {
    try {
        // If fileDataArray is not provided or is empty, return existing state data
        if (!Array.isArray(fileDataArray) || fileDataArray.length === 0) {
            return {
                noChanges: true , data: null
            };  
        }

        
        //create a new array only where item.driver?.avatarFile?.fileKey has value and is not repeated
        const uniqueFileKeys = new Set();
        const presignedArray = fileDataArray.map(item => ({
            id: item.driver?.avatarFile?.id,
            fileKey: item.driver?.avatarFile?.fileKey
        })).filter(item => item.fileKey && !uniqueFileKeys.has(item.fileKey) && uniqueFileKeys.add(item.fileKey)); //filter out items without fileKey
      
        // For each item that needs a presignedUrl, call the GraphQL query in parallel
        const results = await Promise.all(
            presignedArray.map(item =>
            clientQuery(
                `query($fileKey: String!, $timeout: Int) {
                data: getSecureFile(fileKey: $fileKey, timeout: $timeout)
                }`,
                {
                fileKey: item.fileKey,
                timeout: 600, // 10 minutes in seconds
                },
                endpoints.GRAPHQL_GENERAL_V2
            ).then(resp => resp?.data)
            )
        );
        
        return results && results.length > 0
            ? { noChanges: false, data: presignedArray.map((item, index) => ({
                id: item.id,
                fileKey: item.fileKey,
                presignedUrl: results[index]
            })) }
            : { noChanges: true , data: null };
        
        } catch (exc) {
        return rejectWithValue(exc);
        }
    }
);


export const RoutesTrackerRedux = createSlice({
    name: route,
    initialState: INIT_STATE,
    reducers: {
        reset: (state, action) => {
            return action.payload
                ? { ...state, ...action.payload }
                : { ...INIT_STATE };
        },
        searchRoutesTrackerRefresh: (state, action) => {
            return {
                ...state,
                searchRoutesTracker: action.payload
                
            }
        },
    },
    extraReducers: (builder) => {

        builder.addCase(routesTrackerSearchRoutes.pending, (state, action) => {
            state.loadingRoutes = true;
        });
        builder.addCase(routesTrackerSearchRoutes.fulfilled, (state, action) => {
            state.routes = action?.payload;
            state.loadingRoutes = false;

        } );
        builder.addCase(routesTrackerSearchRoutes.rejected, (state, action) => {  
            state.detail = [];
            state.erorrRoutes = action.payload;
            state.loadingRoutes = false;

        });

        

        builder.addCase(routesTrackerSearchRoutesWithFilters.pending, (state, action) => {
            state.loadingRoutes = true;
        });
        builder.addCase(routesTrackerSearchRoutesWithFilters.fulfilled, (state, action) => {
            state.routes = action?.payload;
            state.loadingRoutes = false;

        } );
        builder.addCase(routesTrackerSearchRoutesWithFilters.rejected, (state, action) => {  
            state.detail = [];
            state.erorrRoutes = action.payload;
            state.loadingRoutes = false;

        });

        builder.addCase(getPresignedUrlsForDriverImages.pending, (state, action) => {
            state.loadingRoute = true;

        });
        builder.addCase(getPresignedUrlsForDriverImages.fulfilled, (state, action) => {
            if(action.payload?.noChanges){
                state.loadingRoute = false;
                return;
            }
            const newDriverAvatarFiles = action.payload?.data || [];
            let newRoutesArray = [...state.routes.items];
            newRoutesArray = newRoutesArray.map(routeItem => {
                if(routeItem?.driver?.avatarFile){
                    const updatedFile = newDriverAvatarFiles.find(fileItem => fileItem.id === routeItem.driver.avatarFile.id);
                    if(updatedFile){
                        return {
                            ...routeItem,
                            driver: {
                                ...routeItem.driver,
                                avatarFile: {
                                    ...routeItem.driver.avatarFile,
                                    presignedUrl: updatedFile.presignedUrl
                                }
                            }
                        };
                    }
                }
                return routeItem;
            });

            state.routes.items = newRoutesArray;
            state.loadingRoute = false;

        } );
        builder.addCase(getPresignedUrlsForDriverImages.rejected, (state, action) => {  
            state.erorrRoutes = action.payload;
            state.loadingRoute = false;
        });
        
    }
})

const { actions, reducer } = RoutesTrackerRedux;

export const { reset, searchRoutesTrackerRefresh } = actions;

export default reducer;